import { Button } from '@base-ui/react/button';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { PROFILE_TYPE_LABELS } from '@/admin/sitecontent/conditions';
import { MutationError } from '@/admin/sitecontent/errors';
import { HelpSectionForm } from '@/admin/sitecontent/HelpSectionForm';
import { invalidateHelp } from '@/admin/sitecontent/invalidate';
import { OrderedList } from '@/admin/sitecontent/OrderedList';
import {
  HeaderActions,
  HeaderRow,
  StatusMessage,
} from '@/admin/sitecontent/StatusMessage';
import buttonStyles from '@/admin/styles/button.module.css';
import { Badge } from '@/admin/ui/Badge';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { DetailPage } from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { BackLink } from '@/admin/ui/NavLinks';
import { PageTitle } from '@/admin/ui/Page';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api from '@/api';

export const Route = createFileRoute('/admin/help/sections/$sectionId')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: validateEditSearch,
  component: HelpSectionDetail,
});

function HelpSectionDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sectionId } = Route.useParams();
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const section = api.getAdminHelpSection.useQuery({
    path: { section_id: Number(sectionId) },
  });
  const reorder = api.reorderAdminHelpSectionArticles.useMutation(undefined, {
    onSuccess: () => invalidateHelp(),
  });
  const remove = api.deleteAdminHelpSection.useMutation(undefined, {
    onSuccess: () => {
      invalidateHelp();
      navigate({ to: '/admin/help/sections/' });
    },
  });

  if (section.isLoading)
    return <StatusMessage>{t('admin.help.loadingSection')}</StatusMessage>;
  if (section.error || !section.data)
    return (
      <StatusMessage $error>
        {t('admin.help.errorLoadingSection')}
      </StatusMessage>
    );
  const data = section.data;

  return (
    <DetailPage>
      <BackLink>
        <Link to="/admin/help/sections/">{t('admin.help.backToSections')}</Link>
      </BackLink>
      <HeaderRow>
        <PageTitle>{data.name}</PageTitle>
        {!editing && (
          <HeaderActions>
            <Button className={buttonStyles.Button} onClick={startEditing}>
              {t('admin.common.edit')}
            </Button>
            <Button
              className={buttonStyles.Button}
              onClick={() => setConfirmingDelete(true)}
            >
              {t('admin.common.delete')}
            </Button>
          </HeaderActions>
        )}
      </HeaderRow>

      {editing ? (
        <HelpSectionForm
          key={data.id}
          section={data}
          onSaved={stopEditing}
          onCancel={stopEditing}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>{t('admin.common.details')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{data.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.slug')}</FieldLabel>
                <FieldValue>{data.slug}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.language')}</FieldLabel>
                <FieldValue>{t(LANGUAGE_LABELS[data.language])}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.order')}</FieldLabel>
                <FieldValue>{data.sort_order ?? '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.help.hiddenFrom')}</FieldLabel>
                <FieldValue>
                  {data.excluded_profile_types === null ? (
                    <Badge $variant="warning">
                      {t('admin.help.notInGlobalHelp')}
                    </Badge>
                  ) : data.excluded_profile_types.length === 0 ? (
                    t('admin.help.hiddenFromNobody')
                  ) : (
                    data.excluded_profile_types
                      .map((type) => t(PROFILE_TYPE_LABELS[type]))
                      .join(', ')
                  )}
                </FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <HeaderRow>
              <SectionTitle>{t('admin.help.articles')}</SectionTitle>
              <Link
                to="/admin/help/new/"
                search={{ section: data.id }}
                className={buttonStyles.Button}
              >
                {t('admin.help.newArticleHere')}
              </Link>
            </HeaderRow>
            {data.articles.length === 0 ? (
              <Empty>{t('admin.help.noArticles')}</Empty>
            ) : (
              <OrderedList
                items={data.articles.map((a) => ({
                  id: a.id,
                  label: (
                    <>
                      <Link
                        to="/admin/help/$articleId/"
                        params={{ articleId: String(a.id) }}
                      >
                        {a.title}
                      </Link>{' '}
                      {!a.is_published && (
                        <Badge $variant="neutral">
                          {t('admin.help.statusDraft')}
                        </Badge>
                      )}{' '}
                      {a.needs_rewrite && (
                        <Badge $variant="warning">
                          {t('admin.help.needsRewrite')}
                        </Badge>
                      )}
                    </>
                  ),
                }))}
                rowLabel={(item) =>
                  data.articles.find((a) => a.id === item.id)?.title ?? ''
                }
                saving={reorder.isPending}
                error={reorder.error}
                errorArea="help"
                onSave={(ids) =>
                  reorder.mutate({
                    path: { section_id: data.id },
                    body: { article_ids: ids },
                  })
                }
              />
            )}
          </Section>

          <Section>
            <SectionTitle>{t('admin.help.placementsUsing')}</SectionTitle>
            {data.placements.length === 0 ? (
              <Empty>{t('admin.help.noPlacements')}</Empty>
            ) : (
              <Chips>
                {data.placements.map((placement) => (
                  <Chip key={placement}>{placement}</Chip>
                ))}
              </Chips>
            )}
          </Section>
        </>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title={t('admin.help.deleteSectionTitle')}
        confirmLabel={t('admin.common.delete')}
        tone="danger"
        pending={remove.isPending}
        onConfirm={() => remove.mutate({ path: { section_id: data.id } })}
        onCancel={() => setConfirmingDelete(false)}
      >
        <p>{t('admin.help.deleteSectionBody')}</p>
        <MutationError area="help" error={remove.error} />
      </ConfirmDialog>
    </DetailPage>
  );
}

const Empty = styled.p`
  color: var(--grey-600);
  margin: var(--component-margin);
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: var(--component-margin);
`;

const Chip = styled.code`
  background: var(--grey-100);
  border: 1px solid var(--grey-300);
  border-radius: 4px;
  font-size: 0.8125rem;
  padding: 0.25rem 0.5rem;
`;
